import React from 'react';
import { IconButton, Box, Button } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import UnitPageShell from '../../components/shared/UnitPageShell';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import ZoomableImage from '../../components/shared/ZoomableImage';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride } from '../../services/unitOverridesService';
import { useNavigate } from 'react-router-dom';

const PAGE_ID = 'm2_u2_x1_hk';

const images = [
  { key: 'hk_fbh', src: '/assets/drawable/hk_fbh.PNG', alt: 'Heizkreis Fußbodenheizung', label: 'HK FBH' }
];

const textBlocks = [
  {
    id: 'block_anlagenelemente',
    heading: 'Anlagenelemente',
    body: 'Heizkreis mit Pumpe, Regelventil, Sicherheitstemperaturbegrenzer, Vorlauffühler und Rücklauffühler.'
  },
  {
    id: 'block_regelung',
    heading: 'Regelung',
    body: 'Witterungsgeführte Vorlauftemperaturregelungen mit Maximalbegrenzung der Vorlauftemperatur, Absenkbetrieb, Abschaltung und Pumpenblockierschutz.'
  },
  {
    id: 'block_funktionsbeschreibung',
    heading: 'Funktionsbeschreibung',
    body:
      'Die Vorlauftemperatur (B1) wird über die Außentemperatur geregelt. Für die meisten Heizkreise, die witterungsabhängig geregelt werden, gibt es dafür eine Heizkurve. ' +
      'Unsere Heizkurve wird in der Regel auf der Startseite eingestellt, die dann für alle Heizkreise in der Regelung gilt. ' +
      'Standardmäßig ist die Heizkurve so eingestellt, dass der Heizkreis bei -12°C die maximale Vorlauftemperatur erreichen will und bei 16°C die Mindest-Vorlauftemperatur. ' +
      'Die minimale und die maximale Vorlauftemperatur wird mittels Sollwert im Programm oder auf der GLT eingegeben. ' +
      'Entsprechend der aktuellen Außentemperatur und den beiden eingestellten Grenzwerten Min- und Max-Sollwert wird dann der momentane Vorlaufsollwert errechnet. ' +
      'Für den Absenkbetrieb kann eine Absenkung vorgegeben werden.'
  },
  {
    id: 'block_pumpe_betrieb',
    heading: 'Pumpenbetrieb und Regelfreigabe',
    body:
      'Erst wenn die Rückmeldung (Betrieb) der Pumpe (P1) im Programm erfasst wird, wird der Heizkreis aktiv und nur dann wird die entsprechende Regelung freigegeben. ' +
      'Sollte die Pumpe (P1) keine Betriebsmeldung oder Störmeldung rausgeben, wird diese im Schrank durch das Schalten eines Relais erzeugt.\n\n' +
      'Abhängig von der Regelabweichung des Vorlauftemperaturfühlers (B1) zu dem errechneten Sollwert wird das Ventil (Y1) angesteuert. ' +
      'In den meisten Fällen wird das Signal über 0 bis 10 Volt an das Ventil weitergeleitet. Dies hängt von dem eingebauten Stellantrieb für das Ventil ab.'
  },
  {
    id: 'block_ruecklauf',
    heading: 'Rücklauftemperaturbegrenzung',
    body:
      'Bei manchen Heizkreisen wird auch die Rücklauftemperatur begrenzt. Das bedeutet, dass wenn die Rücklauftemperatur (B2) zu hoch ist, ' +
      'das Regelventil anfängt zu schließen, auch wenn die Vorlauftemperatur nicht erreicht wird.\n\n' +
      'Das wird oft verwendet, wenn die Quelle des Heizmediums ein Brennwertkessel ist oder bei Fernwärme, wenn der Zulieferer Brennwertkessel im Betrieb hat. ' +
      'Auch die Wärmepumpe arbeitet effizienter mit einer niedrigen Rücklauftemperatur.'
  },
  {
    id: 'block_abschaltung',
    heading: 'Abschaltung und Intervallschaltung',
    body:
      'Bei Überschreitung der Außentemperatur des entsprechenden Sollwertes "Heizung Tag Aus" und "Heizung Nacht Aus" erfolgt die Abschaltung des Heizkreises (Pumpe P1 aus – Ventil Y1 zu).\n\n' +
      'Eine Intervallschaltung sorgt dafür, dass die Pumpe wöchentlich für einige Minuten startet, um zu verhindern, dass sich zu viel Dreck ablagert und die Pumpe blockiert.\n\n' +
      'Über eine Schaltuhr wechselt der Heizkreis zwischen Komfort- und Absenkbetrieb. ' +
      'Sollte sich die Anlage im Sommerbetrieb befinden, schaltet der Heizkreis trotz aktiver Schaltuhr in den Absenkbetrieb.'
  },
  {
    id: 'block_ueberwachung',
    heading: 'Überwachung der Vorlauftemperatur',
    body:
      'Ein Sicherheitstemperaturwächter oder Begrenzer (K1) im Vorlauf überwacht die Temperatur. ' +
      'Überschreitet die Temperatur den eingestellten Sollwert, wird das Ventil (Y1) geschlossen, aber die Pumpe bleibt im Betrieb. Es wird eine Störung gemeldet. ' +
      'Bei einem Begrenzer muss aktiv am Gerät die Störung quittiert werden, um den Normalbetrieb wieder zu ermöglichen. ' +
      'Bei einem Wächter wechselt der Heizkreis nach Abkühlung des Vorlaufs automatisch wieder in den Normalbetrieb.'
  },
  {
    id: 'block_notbedien',
    heading: 'Notbedienebene',
    body:
      'Auf der Türe oder im Schaltschrank gibt es in den meisten Fällen eine Notbedienebene, um die Regeleinheiten Pumpe und Ventil manuell zu übersteuern.'
  }
];

const M2_U2_X1_HK: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM2, canUploadImagesM2 } = useAuth();
  const canEditTexts = canEditTextsM2;
  const canUploadImages = canUploadImagesM2;

  const { override, refresh } = useUnitOverride(PAGE_ID);

  const blocks = React.useMemo(() => {
    return textBlocks.map(tb => ({
      ...tb,
      body: override?.texts?.[tb.id] ?? tb.body
    }));
  }, [override]);

  const fullText = React.useMemo(() => blocks.map(b => `${b.heading}. ${b.body}`).join(' '), [blocks]);
  const { speak, stop, speaking } = useTTS({ text: fullText });

  const imgSrc = (slot: string, fallback: string) => {
    const ovr = override?.images?.[slot];
    if (!ovr) return fallback;
    const s = String(ovr);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const handleTextSave = React.useCallback(async (blockId: string, body: string) => {
    if (!token) return alert('Kein Token.');
    if (!canEditTexts) return alert('Keine Berechtigung zum Texte bearbeiten.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockId]: body } }, token);
      alert('Text gespeichert.');
      refresh();
    } catch (e: any) {
      alert('Speichern fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh, canEditTexts]);

  const handleImageReplace = React.useCallback(async (key: string, file: File) => {
    if (!token) return alert('Kein Token.');
    if (!canUploadImages) return alert('Keine Berechtigung fuer Bilder-Uploads.');
    try {
      await uploadUnitAsset(PAGE_ID, key, file, token);
      alert('Bild aktualisiert.');
      refresh();
    } catch (e: any) {
      alert('Upload fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh, canUploadImages]);

  const actions = React.createElement(IconButton, {
    onClick: speaking ? stop : speak,
    'aria-label': 'Vorlesen'
  }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(UnitPageShell, { title: '2.2.1 Heizkreis', actions },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, gap: 2 } },
      // Linke Spalte: Bilder
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        images.map(img => React.createElement(ZoomableImage, {
          key: img.key,
          src: imgSrc(img.key, img.src),
          alt: img.alt,
          label: 'Bild einfügen',
          onReplace: canUploadImages ? (file: File) => handleImageReplace(img.key, file) : undefined
        }))
      ),
      // Rechte Spalte: Textblöcke
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        blocks.map(tb => React.createElement(EditableTextBlock, {
          key: tb.id,
          pageId: PAGE_ID,
          blockId: tb.id,
          heading: tb.heading,
          body: tb.body,
          onSave: (body: string) => handleTextSave(tb.id, body)
        })),
        React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } },
          React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m2_u2_x99_quiz') }, 'Zum Quiz')
        )
      )
    )
  );
};

export default M2_U2_X1_HK;

